/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2025-02-24 15:39:41                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-03-03 15:02:55                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Services
  * Components
  * UsersTab - display users in table
    * useEffect: get users list 
*/

/* Imports */
import { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
/***/

/* Services */
import { api } from "../../../services/api/api";
/***/

/* Components */
import { Table } from "../../../components/table/Table";
import Loading from "../../../components/loading/Loading";
import UserListItem from "../../../components/data/user/UserListItem";
import { Pagination } from "../../../components/pagination/Pagination";
import ExportButton from "../../../components/export-button/ExportButton";
import { userExportDescriptor } from "../../../services/export/data/userExport";
import { t } from "../../../i18n";
import { useTablePageSize } from "../../../hooks/useTablePageSize";
/***/

/**
* UsersTab - display users in table
*/
export default function UsersTab({
  isAdminComponent
}: {
  isAdminComponent?: boolean
}) {
  const [users, setUsers] = useState<EasyTech.User[] | null>(null);
  const navigate = useNavigate();

  const [pageState, setPageState] = useState<number>(0);
  const [orderState, setOrderState] = useState<{ head: string, orderDesc: boolean }>({ head: "lastName", orderDesc: false });

  const onOrderChange = (head: string, orderDesc: boolean) => {
    setOrderState({ head, orderDesc });
    setPageState(0); // Réinitialiser la page lors d'un changement de tri
  };

  const TABLE_HEAD: {label: string, orderKey: string | null, width?: string}[] = [
    {
      label: t("common:lastName"),
      orderKey: "lastName",
      width: "20%"
    },{
      label: t("common:firstName"),
      orderKey: "firstName",
      width: "20%"
    },{
      label: t("common:email"),
      orderKey: "email",
      width: "30%"
    },{
      label: t("common:phone"),
      orderKey: "phone",
      width: "10%"
    },{
      label: t("common:roles"),
      orderKey: "roles",
      width: "20%"
    }
  ];

  if (isAdminComponent) TABLE_HEAD.push({label: t("common:actions"), orderKey: null, width: "100px" });

  const pageSize = useTablePageSize();

  /* Un changement de taille invalide le curseur de page : l'élément 181 est en page 9 à 20 par page
   * et en page 2 à 100. Repartir de la première page est le seul comportement qui ne montre jamais
   * une page vide. */
  useEffect(() => { setPageState(0); }, [pageSize]);

  const loadUsers = useCallback(() => {
    Promise.all([
      api.get("admin/users"),
      api.get("admin/technicians")
    ]).then(([usersData, techsData]) => {
      const allUsers = [...(usersData as EasyTech.User[]), ...(techsData as EasyTech.User[])];
      
      // Dédoublonnage par ID
      const uniqueUsers = Array.from(
        new Map(allUsers.map(user => [user.id, user])).values()
      );

      // Ne garder que ceux qui ont un rôle de type "Plannificateur / Admin"
      // (Les techniciens purs restent dans l'onglet Techniciens uniquement)
      const usersWithAdminRoles = uniqueUsers.filter(user => 
        user.roles.some(role => 
          ["ROLE_ADMIN", "ROLE_PLANNER", "ROLE_SUPER_ADMIN"].includes(role)
        )
      );

      setUsers(usersWithAdminRoles);
    }).catch((err) => {
      console.error(err);
      setUsers([]);
    });
  }, []);

  /**
  * useEffect: get users list 
  * on init
  */
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);
  /***/

  const sortedUsers = useMemo(() => {
    if (!users) return null;
    return [...users].sort((a: any, b: any) => {
      const key = orderState.head as keyof EasyTech.User;
      let valA: any = a[key] != null ? a[key] : "";
      let valB: any = b[key] != null ? b[key] : "";
      
      if (Array.isArray(valA)) valA = valA.join(", ");
      if (Array.isArray(valB)) valB = valB.join(", ");

      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();

      return orderState.orderDesc 
        ? strB.localeCompare(strA)
        : strA.localeCompare(strB);
    });
  }, [users, orderState]);

  const pagedUsers = useMemo(() => {
    if (!sortedUsers) return null;
    return sortedUsers.slice(pageState * pageSize, (pageState + 1) * pageSize);
  }, [sortedUsers, pageState, pageSize]);

  const totalPages = useMemo(() => {
    if (!sortedUsers) return 0;
    return Math.ceil(sortedUsers.length / pageSize);
  }, [sortedUsers, pageSize]);

  return (
    <div className="my-4 relative min-h-48">
      {pagedUsers ?
        <>
          {/*
            * `rows` rather than a search: this tab has no search endpoint behind it, it fetches
            * everything and filters by role in the client. It hands over `sortedUsers` — the whole
            * role-filtered set in the displayed order — not `pagedUsers`, which would export only
            * the page on screen, and not `users`, which would lose the sort.
            */}
          <div className="flex justify-end mb-2">
            <ExportButton<EasyTech.User>
              entityName="users"
              fileName="users.xlsx"
              descriptor={userExportDescriptor}
              rows={sortedUsers ?? []}
            />
          </div>

          <Table  tableHead={TABLE_HEAD}
                  emptyTable={(!pagedUsers || pagedUsers.length === 0) ? "No User" : false}
                  selectedHead={orderState.head}
                  orderDesc={orderState.orderDesc}
                  onHeadClick={onOrderChange}
                  enableRowLinks>
            {pagedUsers.map((user, index) => (
              <UserListItem key={user.id}
                            user={user}
                            isLast={index === pagedUsers.length - 1}
                            onClick={() => navigate(`/users/${user.id}`)}
                            onChange={loadUsers}
                            isAdminComponent={isAdminComponent === undefined ? false : isAdminComponent} />
            ))}
          </Table>
          
          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination
                actualPage={pageState}
                pageNumber={totalPages}
                changePage={setPageState}
              />
            </div>
          )}
        </>
      : <Loading/>}
    </div>
  );
}
/***/
